/**
 * Persona Kütüphanesi — saf tip katmanı (I/O YOK).
 *
 * Wire sözleşmesi `api/routes_persona_library.py` ile birebir eşleşir.
 * Backend camelCase üretir (serialiser'lar orada elle yazılmıştır), bu
 * yüzden burada dönüştürme katmanı YOKTUR — gelen JSON doğrudan bu
 * tiplerdir.
 *
 * ÖNEMLİ ayrım: `LibraryPersona` KALICI bir kimliktir; `domain/types.ts`
 * içindeki `Persona` ise bir tartışmanın koltuğunda oturan RUNTIME
 * örneğidir. Bir kütüphane personası N runtime personası doğurur.
 */

import type { AgentRole, DebateTopology } from "./types";

// ---------------------------------------------------------------------------
// Psikometri
// ---------------------------------------------------------------------------

/** Backend'in `psychometrics.describe_axes()` çıktısı — eksen kataloğu. */
export interface PsychometricAxis {
  key: string;
  group: "ocean" | "debate" | "cognitive" | "rhetoric" | "social";
  label: string;
  description: string;
  lowLabel: string;
  highLabel: string;
  neutral: number;
}

/** Eksen anahtarı → 0..100 değer. Nötr (50) prompt'a hiçbir şey eklemez. */
export type PsychometricProfile = Record<string, number>;

export const AXIS_GROUP_LABELS: Record<PsychometricAxis["group"], string> = {
  ocean: "Temel Kişilik (Big Five)",
  debate: "Müzakere Davranışı",
  cognitive: "Bilişsel Stil",
  rhetoric: "Anlatım ve Dil",
  social: "Sosyal Duruş",
};

export const AXIS_GROUP_ORDER: readonly PsychometricAxis["group"][] = [
  "ocean",
  "debate",
  "cognitive",
  "rhetoric",
  "social",
];

// ---------------------------------------------------------------------------
// Organizasyon ağacı (kurum → bölüm → departman → birim → ekip)
// ---------------------------------------------------------------------------

export type UnitKind =
  | "organization"
  | "division"
  | "department"
  | "unit"
  | "team-cell";

export const UNIT_KIND_LABELS: Record<UnitKind, string> = {
  organization: "Kurum",
  division: "Bölüm",
  department: "Departman",
  unit: "Birim",
  "team-cell": "Ekip",
};

export const UNIT_KIND_ICONS: Record<UnitKind, string> = {
  organization: "🏛",
  division: "🏢",
  department: "🗂",
  unit: "📁",
  "team-cell": "👥",
};

export interface PersonaUnit {
  id: string;
  parentId: string | null;
  name: string;
  description: string;
  kind: UnitKind;
  mission: string;
  accentColor: string;
  /** Materialised path ("/kök/çocuk/") — alt ağaç testleri için. */
  path: string;
  depth: number;
  sortOrder: number;
  isArchived: boolean;
  debateCount: number;
  /** Doğrudan bu düğüme atanmış persona sayısı. */
  personaCount: number;
  /** Alt ağaç dahil toplam. */
  totalCount: number;
}

/** Düz listeyi ağaç sırasına göre gruplar (backend zaten `path` ile sıralı verir). */
export function unitChildren(units: PersonaUnit[], parentId: string | null): PersonaUnit[] {
  return units
    .filter((u) => u.parentId === parentId)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, "tr"));
}

/** Bir düğümün kökten kendisine kadar olan zinciri (breadcrumb için). */
export function unitAncestors(units: PersonaUnit[], unitId: string): PersonaUnit[] {
  const byId = new Map(units.map((u) => [u.id, u]));
  const chain: PersonaUnit[] = [];
  let cursor = byId.get(unitId);
  // Bozuk bir `parentId` döngüsü sonsuz döngüye girmesin — ağaç derinliği
  // sunucuda 5 ile sınırlı, 16 fazlasıyla güvenli bir üst sınır.
  let guard = 0;
  while (cursor && guard < 16) {
    chain.unshift(cursor);
    cursor = cursor.parentId ? byId.get(cursor.parentId) : undefined;
    guard += 1;
  }
  return chain;
}

/** Ayraçlı tam yol: "Cumhurbaşkanlığı › SSB › Silah Sistemleri". */
export function unitPathLabel(
  units: PersonaUnit[],
  unitId: string | null,
  { separator = " › " }: { separator?: string } = {},
): string {
  if (!unitId) return "";
  return unitAncestors(units, unitId)
    .map((u) => u.name)
    .join(separator);
}

// ---------------------------------------------------------------------------
// Demografi / özgeçmiş
// ---------------------------------------------------------------------------

/**
 * Biyografik profil — personanın YARGISININ NEREDEN GELDİĞİNİ belirler.
 *
 * Mizaç (psikometri) *nasıl* tartıştığını söyler; demografi hangi deneyimden
 * konuştuğunu. 15 yıl deneyim farkı, aynı mizaçtaki iki uzmanı gerçekten
 * farklı sonuçlara götürür.
 *
 * Tüm alanlar opsiyoneldir ve boş bırakıldığında prompt'a HİÇBİR ŞEY
 * eklemez. `gender` bilinçli olarak davranışa çevrilmez — yalnız hitap
 * (Bey/Hanım), TTS sesi ve avatar için kullanılır.
 */
export interface PersonaDemographics {
  age: number | null;
  experienceYears: number | null;
  seniority: string | null;
  education: string | null;
  educationField: string | null;
  backgroundSector: string | null;
  regionScope: string | null;
  location: string | null;
  languages: string[];
  certifications: string[];
  careerHighlights: string[];
}

export interface DemographicOption {
  key: string;
  label: string;
}

export interface DemographicOptions {
  seniority: DemographicOption[];
  education: DemographicOption[];
  backgroundSector: DemographicOption[];
  regionScope: DemographicOption[];
  ageRange: { min: number; max: number };
  experienceMax: number;
}

export const EMPTY_DEMOGRAPHICS: PersonaDemographics = {
  age: null,
  experienceYears: null,
  seniority: null,
  education: null,
  educationField: null,
  backgroundSector: null,
  regionScope: null,
  location: null,
  languages: [],
  certifications: [],
  careerHighlights: [],
};

export const GENDER_LABELS: Record<"M" | "F", string> = {
  M: "Erkek",
  F: "Kadın",
};

// ---------------------------------------------------------------------------
// Kimlik
// ---------------------------------------------------------------------------

export type PersonaOrigin =
  | "manual"
  | "llm-synthesized"
  | "cloned"
  | "imported-from-debate";

export interface PersonaSamplingOverrides {
  temperature?: number;
  topP?: number;
  maxTokens?: number;
  presencePenalty?: number;
  frequencyPenalty?: number;
}

export interface PersonaAsymmetry {
  sources?: string[];
  blindSpots?: string[];
  confidenceCeiling?: number;
}

/** Reflexion öz-bilgi kartı — geri bildirimden türetilir, elle yazılmaz. */
export interface PersonaSelfModel {
  strengths?: string[];
  weaknesses?: string[];
  updatedAt?: string;
}

export interface PersonaMemoryConfig {
  topK?: number;
  autoCapture?: boolean;
  autoReflect?: boolean;
  useDocuments?: boolean;
  useLessons?: boolean;
}

export interface PersonaStats {
  debateCount: number;
  turnCount: number;
  likeCount: number;
  dislikeCount: number;
  ratingAvg: number | null;
  ratingCount: number;
  memoryCount: number;
}

/** Liste görünümünün ihtiyaç duyduğu alanlar (backend `brief=true`). */
export interface LibraryPersonaBrief {
  id: string;
  title: string;
  displayName: string;
  role: AgentRole;
  expertise: string | null;
  honorific: "Bey" | "Hanım";
  gender: "M" | "F" | null;
  summary: string;
  avatarUrl: string | null;
  themeColor: string;
  tags: string[];
  /** Organizasyon ağacındaki yeri; null ⇒ atanmamış. */
  unitId: string | null;
  origin: PersonaOrigin;
  isArchived: boolean;
  revision: number;
  stats: PersonaStats;
  /** Tek satırlık mizaç özeti, ör. "Muhalif · Sorgulayıcı · Titiz". */
  temperament: string;
  /** Tek satırlık özgeçmiş özeti, ör. "52 yaş · 27 yıl deneyim · Kamu". */
  biography: string;
  selfModel: PersonaSelfModel;
  lastUsedAt: string | null;
  updatedAt: string;
  createdAt: string;
}

/** Detay ekranının tamamı (backend `brief=false`). */
export interface LibraryPersona extends LibraryPersonaBrief {
  systemInstruction: string;
  charter: string;
  voiceId: string;
  voiceRate: string;
  voicePitch: string;
  voiceVolume: string;
  modelId: string | null;
  samplingOverrides: PersonaSamplingOverrides;
  asymmetry: PersonaAsymmetry;
  psychometrics: PsychometricProfile;
  demographics: PersonaDemographics;
  memoryConfig: PersonaMemoryConfig;
  sourcePersonaId: string | null;
}

/** `POST /personas/synthesize` çıktısı — HENÜZ KAYDEDİLMEMİŞ taslak. */
export interface PersonaDraft {
  title: string;
  displayName: string;
  role: AgentRole;
  expertise: string;
  honorific: "Bey" | "Hanım";
  gender: "M" | "F";
  summary: string;
  charter: string;
  systemInstruction: string;
  tags: string[];
  psychometrics: PsychometricProfile;
  themeColor: string;
  voiceId: string;
  avatarUrl: string;
  origin: PersonaOrigin;
}

export interface PersonaRevision {
  revision: number;
  snapshot: Record<string, unknown>;
  changedBy: string;
  changeNote: string;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Hafıza
// ---------------------------------------------------------------------------

export type MemoryKind = "episodic" | "semantic" | "reflection" | "procedural";

export const MEMORY_KIND_LABELS: Record<MemoryKind, string> = {
  episodic: "Yaşantı",
  semantic: "Kalıcı Bilgi",
  reflection: "Öz Çıkarım",
  procedural: "Çalışma İlkesi",
};

export const MEMORY_KIND_HINTS: Record<MemoryKind, string> = {
  episodic: "Belirli bir tartışmada ne savundu, ne oldu.",
  semantic: "Konudan bağımsız, gelecekte de geçerli bilgi.",
  reflection: "Birden fazla yaşantıyı birleştiren üst düzey içgörü.",
  procedural: "Nasıl davranacağına dair öğrenilmiş kural.",
};

export interface PersonaMemory {
  id: string;
  kind: MemoryKind;
  content: string;
  context: string;
  keywords: string[];
  tags: string[];
  /** LLM'in atadığı 0-10 önem (Generative Agents "poignancy"). */
  importance: number;
  confidence: number;
  accessCount: number;
  isPinned: boolean;
  sourceKind: string;
  sourceSessionId: string | null;
  validFrom: string;
  /** Doluysa bu anı geçersiz kılınmıştır (bi-temporal arşiv). */
  validTo: string | null;
  createdAt: string;
  lastAccessedAt: string;
}

export interface PersonaMemoryStats {
  byKind: Partial<Record<MemoryKind, number>>;
  total: number;
  archived: number;
  /** Son çıkarımdan bu yana biriken önem toplamı. */
  pendingImportance: number;
  reflectionThreshold: number;
}

// ---------------------------------------------------------------------------
// Belge hafıza bölümleri
// ---------------------------------------------------------------------------

export type CollectionKind = "knowledge" | "style-exemplar" | "procedure" | "dossier";
export type RetrievalMode = "on-demand" | "always" | "style-only";

export const COLLECTION_KIND_LABELS: Record<CollectionKind, string> = {
  knowledge: "Bilgi Arşivi",
  "style-exemplar": "Stil Örneği",
  procedure: "Prosedür",
  dossier: "Dosya",
};

export const COLLECTION_KIND_HINTS: Record<CollectionKind, string> = {
  knowledge: "Personanın bildiği belgeler; soruyla ilgiliyse turda alıntılanır.",
  "style-exemplar":
    "SİZİN yazdığınız belgeler. İçeriği değil, yazım tarzınız öğrenilir.",
  procedure: "Nasıl çalışacağını anlatan yönerge ve kontrol listeleri.",
  dossier: "Belirli bir konu/aktör hakkında toplanmış dosya.",
};

export const RETRIEVAL_MODE_LABELS: Record<RetrievalMode, string> = {
  "on-demand": "İlgiliyse getir",
  always: "Her turda getir",
  "style-only": "Yalnız stil için",
};

export interface MemoryCollection {
  id: string;
  /** Persona sahipli bölümlerde dolu; birim bölümlerinde null. */
  personaId: string | null;
  /** Birim sahipli bölümlerde dolu. Alt birimlere MİRAS kalır. */
  unitId: string | null;
  name: string;
  description: string;
  kind: CollectionKind;
  retrievalMode: RetrievalMode;
  injectionWeight: number;
  topK: number;
  isActive: boolean;
  docCount: number;
  chunkCount: number;
  sortOrder: number;
  createdAt: string;
}

export interface CollectionDocument {
  id: string;
  filename: string;
  mime: string;
  byteSize: number;
  pageCount: number | null;
  chunkCount: number;
  status: "pending" | "ready" | "failed";
  error: string | null;
  createdAt: string;
  /**
   * True ⇒ indirme orijinal dosyayı verir. False ⇒ bu belge diskte ham
   * bayt arşivinden ÖNCE yüklenmiş (ya da dosya sonradan kaybolmuş);
   * indirme Qdrant parçalarından yeniden kurulan düz metne düşer.
   */
  hasOriginal: boolean;
}

// ---------------------------------------------------------------------------
// Vektör arama — bölüm / birim / persona ölçeğinde
// ---------------------------------------------------------------------------

export interface LibrarySearchHit {
  documentId: string;
  filename: string;
  chunkIndex: number;
  text: string;
  /** Kosinüs benzerliği, 0-1 aralığına yakın (Qdrant skoru). */
  score: number;
}

// ---------------------------------------------------------------------------
// Stil parmak izi
// ---------------------------------------------------------------------------

export interface StyleSpec {
  rhythm?: string;
  register?: string;
  structure?: string;
  openings?: string;
  closings?: string;
  punctuation?: string;
  vocabulary?: string;
  signatureMoves?: string[];
}

export interface StyleExemplar {
  excerpt: string;
  teachingNote: string;
}

export interface StyleProfile {
  status: "idle" | "building" | "ready" | "failed";
  spec: StyleSpec;
  exemplars: StyleExemplar[];
  metrics?: Record<string, number>;
  bannedPhrases?: string[];
  sampleCount: number;
  error?: string | null;
  generatedAt?: string | null;
}

/** Ölçüm anahtarı → Türkçe etiket. Panelde ham JSON göstermemek için. */
export const STYLE_METRIC_LABELS: Record<string, string> = {
  avgSentenceWords: "Ortalama cümle (kelime)",
  medianSentenceWords: "Medyan cümle (kelime)",
  sentenceLengthStdev: "Cümle uzunluğu sapması",
  shortSentenceRatio: "Kısa cümle oranı",
  avgWordChars: "Ortalama kelime (harf)",
  avgParagraphSentences: "Paragraf başına cümle",
  questionRatio: "Soru cümlesi oranı",
  emDashPer1k: "Uzun tire / 1000 kelime",
  parenthesisPer1k: "Parantez / 1000 kelime",
  semicolonPer1k: "Noktalı virgül / 1000 kelime",
  hedgePer1k: "Çekince ifadesi / 1000 kelime",
  connectivePer1k: "Bağlaç / 1000 kelime",
  firstPersonPer1k: "Birinci şahıs / 1000 kelime",
  bulletLineRatio: "Madde işaretli satır oranı",
  acronymPer1k: "Kısaltma / 1000 kelime",
  sampleChars: "Örnek boyutu (karakter)",
  wordCount: "Kelime sayısı",
  sentenceCount: "Cümle sayısı",
  paragraphCount: "Paragraf sayısı",
};

// ---------------------------------------------------------------------------
// Geri bildirim ve gelişim (Reflexion)
// ---------------------------------------------------------------------------

export type FeedbackVerdict = "like" | "dislike" | "neutral";

export interface FeedbackDimension {
  key: string;
  label: string;
}

export interface PersonaFeedback {
  id: string;
  verdict: FeedbackVerdict;
  rating: number | null;
  note: string;
  excerpt: string;
  dimensions: Record<string, number>;
  sessionId: string | null;
  /** true ⇒ bu geri bildirim zaten bir derse dönüştürüldü. */
  processed: boolean;
  createdAt: string;
}

export interface GrowthLesson {
  id: string;
  /** reinforce = "sürdür", correct = "düzelt". */
  polarity: "reinforce" | "correct";
  lesson: string;
  rationale: string;
  /** Kaç ayrı geri bildirimin bu dersi doğruladığı. */
  evidenceCount: number;
  weight: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ConsolidationResult {
  processed: number;
  lessons: number;
  strengths?: string[];
  weaknesses?: string[];
  pending?: number;
  required?: number;
  reason?: string;
}

/** Transkriptteki oy durumu — `GET /sessions/{id}/feedback`. */
export interface SessionFeedbackState {
  feedback: {
    turnId: string | null;
    personaId: string | null;
    verdict: FeedbackVerdict;
    rating: number | null;
    note: string;
    dimensions: Record<string, number>;
  }[];
  /** runtime persona id → kütüphane persona id. Boşsa o konuşmacı öğrenemez. */
  libraryBySpeaker: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Takımlar
// ---------------------------------------------------------------------------

/**
 * Takım — organizasyon şemasını KESEN bir görev gücü.
 *
 * Birimden farkı: birim hiyerarşiktir ve bir personanın tek bir birimi olur;
 * takım ise farklı kurum/birimlerden uzmanları geçici bir kadroda toplar,
 * aynı persona birden çok takımda yer alabilir.
 */
export interface PersonaTeam {
  id: string;
  name: string;
  description: string;
  mission: string;
  /**
   * @deprecated Takım artık topoloji taşımıyor. Bir görev gücü farklı soru
   * tipleri için yeniden kullanılır ve doğru tartışma düzeni soruya göre
   * değişir — sihirbazın 2. adımında seçilir. Alan yalnız eski satırların
   * okunabilmesi için duruyor; YENİ KOD OKUMAMALI.
   */
  defaultTopology: DebateTopology;
  /** @deprecated Bkz. `defaultTopology`. */
  defaultMaxRounds: number | null;
  accentColor: string;
  tags: string[];
  isArchived: boolean;
  debateCount: number;
  lastUsedAt: string | null;
  members: LibraryPersonaBrief[];
}

/** Tartışmaya konseyi hangi kaynaktan alacağımız. */
export type CouncilSource = "auto" | "catalog" | "team" | "unit" | "picked";

export const COUNCIL_SOURCE_LABELS: Record<CouncilSource, string> = {
  auto: "Otomatik Üret",
  catalog: "Adaylardan Seç",
  team: "Kütüphane Takımı",
  unit: "Organizasyon Birimi",
  picked: "Tek Tek Seç",
};

/**
 * Dinamik (bu soruya özel LLM üretimi) konsey kuran kaynaklar.
 *
 * `auto` ve `catalog` aynı aileden: ikisi de `wizard.personas`'ı doldurur.
 * Kütüphane kaynakları ise kalıcı kimlikleri id ile taşır. Ayrımı tek yerde
 * tutmak, "hangi dalda persona listesi bekleniyor?" sorusunu her çağrı
 * yerinde yeniden cevaplamak zorunda kalmayı önler.
 */
export function isDynamicCouncilSource(source: CouncilSource | null): boolean {
  return source === "auto" || source === "catalog";
}

// ---------------------------------------------------------------------------
// Katalog (tek istekte gelen enum/eksen paketi)
// ---------------------------------------------------------------------------

export interface LibraryCatalog {
  axes: PsychometricAxis[];
  demographicOptions: DemographicOptions;
  roles: AgentRole[];
  unitKinds: UnitKind[];
  memoryKinds: MemoryKind[];
  collectionKinds: CollectionKind[];
  retrievalModes: RetrievalMode[];
  feedbackVerdicts: FeedbackVerdict[];
  feedbackDimensions: FeedbackDimension[];
}

/** Detay sayfasını tek round-trip'te dolduran paket. */
export interface PersonaDetailBundle {
  persona: LibraryPersona;
  collections: MemoryCollection[];
  /**
   * Bağlı olduğu birimden ve atalarından miras alınan bölümler — salt
   * okunur. Kullanıcı "belgeyi birime yükledim ama personada göremiyorum"
   * şaşkınlığını yaşamasın diye ayrıca gösterilir.
   */
  inheritedCollections: MemoryCollection[];
  lessons: GrowthLesson[];
  style: StyleProfile;
  memoryStats: PersonaMemoryStats;
  /** Henüz derse dönüştürülmemiş geri bildirim sayısı. */
  pendingFeedback: number;
}

// ---------------------------------------------------------------------------
// Yardımcılar (saf)
// ---------------------------------------------------------------------------

/** Beğeni oranı 0..1; hiç oy yoksa null (0 ile karıştırılmamalı). */
export function approvalRate(stats: PersonaStats): number | null {
  const total = stats.likeCount + stats.dislikeCount;
  return total === 0 ? null : stats.likeCount / total;
}

/** Bir eksenin nötrden anlamlı sapması var mı (backend deadband = 12). */
export function isAxisActive(value: number): boolean {
  return Math.abs(value - 50) > 12;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
